// Favourites event (analytics round 2, fix 6). Why `favButtonClicked` never reached PostHog:
//   - Since the P2 cutover (P2_REACT=1) the workflow hubs and stages, where members read most,
//     render the P2 pages, which have no heart. Their save control is SaveWorkflowButton
//     ("Save workflow"), the only writer of user_preferences kind 'saved_workflow' (the
//     saved items counted in round 1), and it sent no event at all.
//   - The heart (FavouriteButton) rendered for nobody from the 2026-07-14 cutover until the
//     2026-08-26 Supabase rebuild, and since then only on product, vendor, solution and
//     article pages and favourite cards, for signed-in members. Its trackEvent call works
//     once PostHog is loaded (members in Europe who have not clicked Accept are captured
//     cookieless, which PostHog drops unless the project's cookieless setting is on).
// Both controls now send the same event, once per save or unsave that actually reached
// user_preferences, built here so the payload is one shape and unit-tested.
import { contentKindForPath, type ContentKind } from '@/lib/supabase/engagement'

/** Kept from the Clerk era so the Monday report and existing insights find it. */
export const FAVOURITE_EVENT = 'favButtonClicked'

/** user_preferences.kind of the saved item. */
export type SavedItemKind = 'bookmark' | 'saved_workflow'

export interface FavouriteEventProperties {
  action: 'add' | 'remove'
  content_type: ContentKind
  content_path: string
  kind: SavedItemKind
}

/** Path only: no query string or fragment, so nothing but the page is sent. */
export function cleanContentPath(path: string): string {
  const p = path.split(/[?#]/, 1)[0] || '/'
  return p.startsWith('/') ? p : `/${p}`
}

/**
 * The event for a save (saved = true) or unsave of `path`. content_type mirrors what the
 * write stores in user_preferences.content_type: saved workflows are 'workflow', favourites
 * map the path like toggleBookmark does.
 */
export function favouriteEvent(input: {
  path: string
  saved: boolean
  kind: SavedItemKind
}): { name: typeof FAVOURITE_EVENT; properties: FavouriteEventProperties } {
  const content_path = cleanContentPath(input.path)
  const content_type: ContentKind =
    input.kind === 'saved_workflow' ? 'workflow' : contentKindForPath(content_path) || 'page'
  return {
    name: FAVOURITE_EVENT,
    properties: {
      action: input.saved ? 'add' : 'remove',
      content_type,
      content_path,
      kind: input.kind,
    },
  }
}

/**
 * The event to send once the write has resolved, or null. The toggle helpers return the
 * stored state (false also when an insert failed), so an event is sent only when that state
 * is the one the member asked for: one event per change that reached the database.
 */
export function favouriteEventAfterWrite(input: {
  path: string
  wanted: boolean
  stored: boolean
  kind: SavedItemKind
}) {
  if (input.wanted !== input.stored) return null
  return favouriteEvent({ path: input.path, saved: input.stored, kind: input.kind })
}
